import React, { useState, useEffect } from 'react';
import {
  Table,
  Input,
  InputNumber,
  Form,
  PageHeader,
  Switch,
  Row,
  Col,
  Button,
  Icon,
  Divider,
  BackTop
} from 'antd';

import styles from './UserManagement.module.scss';

const UserManagement = ({ isLoading, ...rest }) => {
  const loading = {
    spinning: isLoading,
    tip: 'Fetching user list...',
    size: 'large'
  };

  return (
    <div>
      <EditableFormTable loading={loading} {...rest} />
    </div>
  );
};

const EditableContext = React.createContext();

const EditableCell = props => {
  const getInput = () => {
    const { dataIndex } = props;

    switch (dataIndex) {
      case 'MaxLicenseDay':
        return <InputNumber />;
      case 'Admin':
        return <Switch />;
      default:
        return <Input />;
    }
  };

  const renderCell = ({ getFieldDecorator }) => {
    const { editing, dataIndex, title, inputType, record, index, children, ...restProps } = props;

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item style={{ margin: 0 }}>
            {getFieldDecorator(dataIndex, {
              rules: [
                {
                  required: true,
                  message: `Please Input ${title}!`
                }
              ],
              initialValue: record[dataIndex],
              valuePropName: dataIndex === 'Admin' ? 'checked' : 'value'
            })(getInput())}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  return <EditableContext.Consumer>{renderCell}</EditableContext.Consumer>;
};

const EditableTable = ({ form, data, loading, updateInfo }) => {
  const [editingKey, setEditingKey] = useState('');
  let searchInputRef;

  const getEmailColumnSearchProps = () => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div className={styles.filterDropdown}>
        <Input
          className={styles.textFilterInput}
          ref={node => {
            searchInputRef = node;
          }}
          placeholder={'Type substring of email'}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
        />
        <Button className={styles.textFilterButton} onClick={() => clearFilters()} size="small">
          Reset
        </Button>
        <Button
          className={styles.textFilterSearchButton}
          type="primary"
          onClick={() => confirm()}
          icon="search"
          size="small"
        >
          Search
        </Button>
      </div>
    ),
    filterIcon: filtered => (
      <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) => record.Email.toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInputRef.select());
      }
    }
  });

  const isEditing = record => record.ID === editingKey;

  const cancelEdit = () => {
    setEditingKey('');
  };

  const save = (form, email) => {
    form.validateFields((error, formData) => {
      if (error) {
        return;
      }

      updateInfo(email, formData);
    });

    setEditingKey('');
  };

  const onEditButtonClick = key => {
    setEditingKey(key);
  };

  const columns = [
    {
      title: 'No.',
      dataIndex: 'No',
      editable: false,
      render: (text, record, rowIndex) => {
        return rowIndex + 1;
      }
    },
    {
      title: 'Is Admin',
      dataIndex: 'Admin',
      editable: true,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.Admin.toString().localeCompare(b.Admin.toString()),
      filters: [{ text: 'Yes', value: true }, { text: 'No', value: false }],
      onFilter: (value, record) => record.Admin === value,
      filterMultiple: false,
      render: text => <Switch checked={text} disabled />
    },
    {
      title: 'Email',
      dataIndex: 'Email',
      editable: false,
      sorter: (a, b) => a.Email.localeCompare(b.Email),
      ...getEmailColumnSearchProps()
    },
    {
      title: 'Max License Day',
      dataIndex: 'MaxLicenseDay',
      editable: true,
      sorter: (a, b) => a.MaxLicenseDay - b.MaxLicenseDay
    },
    {
      title: 'Action',
      render: (text, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Button onClick={() => cancelEdit(record.ID)}>Cancel</Button>

            <EditableContext.Consumer>
              {form => (
                <Button
                  type={'primary'}
                  style={{ marginLeft: 8 }}
                  onClick={() => save(form, record.Email)}
                >
                  Save
                </Button>
              )}
            </EditableContext.Consumer>
          </span>
        ) : (
          <Button disabled={editingKey !== ''} onClick={() => onEditButtonClick(record.ID)}>
            Edit
          </Button>
        );
      }
    }
  ];

  const pagination = {
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    onChange: cancelEdit
  };

  const components = {
    body: {
      cell: EditableCell
    }
  };

  const newColumns = columns.map(col => {
    if (!col.editable) {
      return {
        ...col
      };
    }
    return {
      ...col,
      onCell: record => ({
        record,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record)
      })
    };
  });

  return (
    <EditableContext.Provider value={form}>
      <Table
        components={components}
        dataSource={data}
        columns={newColumns}
        rowClassName={styles.editableRow}
        pagination={pagination}
        rowKey={'ID'}
        loading={loading}
      />
    </EditableContext.Provider>
  );
};

const EditableFormTable = Form.create()(EditableTable);

const UserManagementPage = ({
  userToken,
  users,
  isUserListFetching,
  fetchUserList,
  updateUserInfo
}) => {
  useEffect(() => {
    if (userToken) {
      fetchUserList();
    }
  }, [userToken, fetchUserList]);

  return (
    <PageHeader
      title="User Management"
      extra={[
        <Row type="flex" justify="space-between" align="top" key={0}>
          {users.length !== 0 && (
            <>
              <Col>
                <Button onClick={() => fetchUserList()}>Fetch User Data</Button>
              </Col>
            </>
          )}
        </Row>
      ]}
    >
      <Divider type={'horizontal'} />

      <UserManagement data={users} isLoading={isUserListFetching} updateInfo={updateUserInfo} />

      <BackTop visibilityHeight={100} />
    </PageHeader>
  );
};

export default UserManagementPage;
